import { useState, useCallback, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { Package, Users, FileEdit, List, LogOut, Bell, Sparkles, Film, CheckCircle, Zap, FileImage, TrendingUp, Shield, Upload, Truck, UserCog, Menu, X, ClipboardList, Plus, ShieldCheck, Layers, PackageCheck } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import CostTrackerWidget from "@/components/CostTrackerWidget";

const headerTabs = [
  { name: "Dashboard", href: "/", module: "dashboard" },
  { name: "Revenue Analytics", href: "/revenue-analytics", module: "revenue_analytics" },
  { name: "Discount Analytics", href: "/discount-analytics", module: "discount_analytics" },
  { name: "Freebie Analytics", href: "/freebie-analytics", module: "freebie_analytics" },
];

// Extra tabs grouped under the "+" overflow menu. Scalable — add more entries as needed.
const moreTabs = [
  { name: "Remarks Analytics", href: "/remarks-analytics", description: "Reasons behind over-discount", module: "remarks_analytics" },
  { name: "Monthly Sales Analysis", href: "/monthly-sales-analysis", description: "Brand-wise monthly invoice report", module: "monthly_sales_analysis" },
  { name: "Seller Insights", href: "/seller-insights", description: "Performance by seller", module: "seller_insights" },
];

const getSidebarItems = () => {
  // Always include all items; filterByPermissions handles visibility. Admins see all.
  return [
    { name: "Products", href: "/products", icon: Package, module: "products" },
    { name: "Sellers", href: "/sellers", icon: Users, module: "sellers" },
    { name: "Drafts", href: "/drafts", icon: FileEdit, module: "quotations" },
    { name: "Discount Approvals", href: "/discount-approvals", icon: ShieldCheck, module: "discount_approvals" },
    { name: "Quotations Log", href: "/quotations-log", icon: List, module: "quotations" },
    { name: "Dispatch Panel", href: "/dispatch", icon: Truck, module: "dispatch" },
    { name: "Inventory Allocation", href: "/inventory-allocation", icon: ClipboardList, module: "inventory_allocation" },
    { name: "MCF Stock Transfers", href: "/mcf-stock-transfers", icon: PackageCheck, module: "mcf_stock_transfers" },
    { name: "Slab Intelligence", href: "/slab-intelligence", icon: Layers, module: "slab_intelligence" },
    { name: "Control Panel", href: "/users", icon: UserCog, module: "control_panel" },
  ];
};

// Filter nav items by what the current user has permission to see. Admins see all.
const filterByPermissions = (items, user) => {
  if (!user) return [];
  if (user.role === 'admin') return items;
  const perms = user.permissions || [];
  return items.filter((it) => !it.module || perms.includes(it.module));
};

const whatsNewUpdates = [
  { id: 1, icon: Shield, title: "Strict Validations", description: "Transporter mandatory, over-discount approval enforced", date: "Apr 2026", isNew: true },
  { id: 2, icon: TrendingUp, title: "Seller Insights", description: "Top sellers, contribution analysis, geo-analytics", date: "Apr 2026", isNew: true },
  { id: 3, icon: Upload, title: "Bulk Upload Quotations", description: "Upload historical quotation data via Excel", date: "Apr 2026", isNew: true },
  { id: 4, icon: Film, title: "Enhanced Dashboard", description: "Global filters, time-based aggregation", date: "Apr 2026", isNew: false },
  { id: 5, icon: CheckCircle, title: "Brand-wise Discounts", description: "Effective discount % per brand", date: "Apr 2026", isNew: false },
  { id: 6, icon: Zap, title: "Freebie Analytics", description: "Monitor freebie value and top SKUs", date: "Apr 2026", isNew: false },
  { id: 7, icon: Truck, title: "Transporter Management", description: "Add, select, and manage transporters", date: "Apr 2026", isNew: false },
  { id: 8, icon: FileImage, title: "Email Attachments", description: "Custom attachments to quotation emails", date: "Mar 2026", isNew: false },
];

export default function DashboardLayout({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(() => {
    const stored = localStorage.getItem("user");
    return stored ? JSON.parse(stored) : null;
  });
  const [isLoading, setIsLoading] = useState(true);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

  const userIsAdmin = user?.role === "admin";
  const sidebarItems = filterByPermissions(getSidebarItems(), user);
  const visibleHeaderTabs = filterByPermissions(headerTabs, user);
  const visibleMoreTabs = filterByPermissions(moreTabs, user);

  // Remove dark class on mount (cleanup from previous dark mode)
  useEffect(() => {
    document.documentElement.classList.remove("dark");
    localStorage.removeItem("omnix-theme");
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
      localStorage.setItem("user", JSON.stringify(response.data));
    } catch {
      localStorage.removeItem("user");
      localStorage.removeItem("session_token");
      delete axios.defaults.headers.common["Authorization"];
      navigate("/login");
    } finally {
      setIsLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    if (location.state?.user) {
      setUser(location.state.user);
      localStorage.setItem("user", JSON.stringify(location.state.user));
      setIsLoading(false);
      window.history.replaceState({}, document.title);
      return;
    }
    checkAuth();
  }, [checkAuth, location.state]);

  const handleLogout = async () => {
    try { await axios.post(`${API}/auth/logout`); } catch { /* silent */ }
    finally {
      localStorage.removeItem("user");
      localStorage.removeItem("session_token");
      delete axios.defaults.headers.common["Authorization"];
      navigate("/login");
    }
  };

  const isActive = (href) => {
    if (href === "/") return location.pathname === "/";
    return location.pathname.startsWith(href);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-[#32B4AC] border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* ===== TOP HEADER ===== */}
      <header className="sticky top-0 z-50 bg-white border-b border-[#E8E8E8]">
        <div className="flex items-center h-14 px-4">

          {/* LEFT: Logo */}
          <button onClick={() => navigate("/")} className="flex items-center gap-2.5 shrink-0" data-testid="logo-button">
            <img src="/omnix-logo.png" alt="Omnix" className="h-10 w-10 object-contain" />
            <div className="flex flex-col leading-none">
              <span className="text-[22px] font-bold text-[#32B4AC] tracking-wide" style={{ fontFamily: "Manrope, sans-serif" }}>OMNIX</span>
              <span className="text-[10px] text-[#1A1A1A]/50 -mt-0.5">by <span className="text-[#32B4AC] font-medium">wellversed</span><sup className="text-[7px]">&trade;</sup></span>
            </div>
          </button>

          {/* Mobile sidebar toggle */}
          <button onClick={() => setMobileSidebarOpen(!mobileSidebarOpen)} className="md:hidden p-1.5 ml-2 rounded-md hover:bg-[#E8E8E8]/60" data-testid="mobile-menu-button">
            <Menu className="h-5 w-5 text-[#1A1A1A]" />
          </button>

          {/* CENTER-RIGHT: Tabs positioned at ~60-70% using flex basis */}
          <div className="hidden md:flex flex-1 justify-center pl-[10%]">
            <nav className="flex items-center gap-1" data-testid="main-tabs">
              {visibleHeaderTabs.map((tab) => {
                const active = isActive(tab.href);
                return (
                  <button
                    key={tab.name}
                    onClick={() => navigate(tab.href)}
                    className={`px-4 py-1.5 rounded-full text-[13px] font-medium transition-all ${
                      active
                        ? "bg-[#32B4AC] text-white"
                        : "text-[#1A1A1A] hover:bg-[#E8E8E8]/60"
                    }`}
                    data-testid={`tab-${tab.name.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    {tab.name}
                  </button>
                );
              })}
              {/* Overflow "+" menu */}
              {(() => {
                const moreActive = visibleMoreTabs.some((t) => isActive(t.href));
                return (
                  <Popover>
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        className={`flex items-center justify-center h-8 w-8 rounded-full text-[13px] font-medium transition-all border ${
                          moreActive
                            ? "bg-[#32B4AC] text-white border-[#32B4AC]"
                            : "text-[#1A1A1A] bg-transparent border-[#E8E8E8] hover:bg-[#F5F5F5] data-[state=open]:bg-[#32B4AC] data-[state=open]:text-white data-[state=open]:border-[#32B4AC]"
                        }`}
                        aria-label="More tabs"
                        data-testid="tab-more"
                      >
                        <Plus className="h-4 w-4" />
                      </button>
                    </PopoverTrigger>
                    <PopoverContent
                      align="start"
                      sideOffset={6}
                      className="p-1 w-60 rounded-lg border border-[#E8E8E8] shadow-md bg-white"
                      data-testid="more-menu"
                    >
                      {visibleMoreTabs.map((t) => {
                        const active = isActive(t.href);
                        return (
                          <button
                            key={t.href}
                            onClick={() => navigate(t.href)}
                            className="w-full text-left flex items-start gap-2.5 px-4 py-2.5 rounded-md hover:bg-[#F5F5F5] transition-colors"
                            data-testid={`more-menu-item-${t.name.toLowerCase().replace(/\s+/g, "-")}`}
                          >
                            <span
                              className={`mt-1.5 h-1.5 w-1.5 rounded-full flex-shrink-0 transition-colors ${
                                active ? "bg-[#32B4AC]" : "bg-transparent"
                              }`}
                            />
                            <span className="flex flex-col">
                              <span className="text-[13px] font-medium text-[#1A1A1A] leading-tight">{t.name}</span>
                              {t.description && (
                                <span className="text-[11px] text-[#888780] mt-0.5">{t.description}</span>
                              )}
                            </span>
                          </button>
                        );
                      })}
                    </PopoverContent>
                  </Popover>
                );
              })()}
            </nav>
          </div>

          {/* RIGHT: CTA + What's New */}
          <div className="flex items-center gap-3 shrink-0">
            {(userIsAdmin || (user?.permissions || []).includes("quotations")) && (
              <button
                onClick={() => navigate("/quotations/create")}
                className="hidden sm:flex items-center bg-[#32B4AC] hover:bg-[#2A9A93] text-white rounded-lg text-[13px] font-medium px-4 h-8 transition-colors"
                data-testid="create-quotation-nav-button"
              >
                Create Quotation
              </button>
            )}

            <CostTrackerWidget />

            <Popover>
              <PopoverTrigger asChild>
                <button className="flex items-center gap-1.5 px-2 py-1.5 rounded-md hover:bg-[#E8E8E8]/60 transition-colors" data-testid="whats-new-bell">
                  <div className="relative">
                    <Bell className="h-[18px] w-[18px] text-[#1A1A1A]" />
                    <span className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 bg-[#32B4AC] rounded-full" />
                  </div>
                  <span className="hidden lg:inline text-[12px] font-medium text-[#1A1A1A]">What&apos;s New</span>
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-80 p-0 rounded-lg shadow-lg border border-[#E8E8E8] bg-white" align="end" sideOffset={8}>
                <div className="p-3 border-b border-[#E8E8E8]">
                  <div className="flex items-center gap-2">
                    <Sparkles className="h-4 w-4 text-[#32B4AC]" />
                    <h3 className="text-[13px] font-semibold text-[#0F0F0F]">What&apos;s New</h3>
                  </div>
                </div>
                <div className="max-h-[360px] overflow-y-auto">
                  {whatsNewUpdates.map((update) => {
                    const Icon = update.icon;
                    return (
                      <div key={update.id} className="flex items-start gap-2.5 px-3 py-2 hover:bg-[#E8E8E8]/30 transition-colors border-b border-[#E8E8E8]/50 last:border-0">
                        <div className="p-1.5 rounded bg-[#E8E8E8]/50 shrink-0 mt-0.5"><Icon className="h-3 w-3 text-[#1A1A1A]" /></div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5">
                            <p className="text-[12px] font-medium text-[#0F0F0F]">{update.title}</p>
                            {update.isNew && <span className="px-1.5 py-px text-[8px] font-bold text-white bg-[#32B4AC] rounded-full">NEW</span>}
                          </div>
                          <p className="text-[11px] text-[#1A1A1A]/50 mt-0.5">{update.description}</p>
                        </div>
                        <span className="text-[10px] text-[#1A1A1A]/30 shrink-0">{update.date}</span>
                      </div>
                    );
                  })}
                </div>
              </PopoverContent>
            </Popover>
          </div>
        </div>
      </header>

      <div className="flex" style={{ height: "calc(100vh - 56px)" }}>
        {/* ===== LEFT SIDEBAR ===== */}
        <aside className="hidden md:flex flex-col w-48 shrink-0 bg-white border-r border-[#E8E8E8]">
          <nav className="flex-1 px-2 py-3 space-y-0.5">
            {sidebarItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.href);
              return (
                <button
                  key={item.name}
                  onClick={() => navigate(item.href)}
                  className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-md text-[13px] font-medium text-left transition-colors ${
                    active
                      ? "bg-[#32B4AC] text-white"
                      : "text-[#1A1A1A] hover:bg-[#E8E8E8]/60"
                  }`}
                  data-testid={`sidebar-${item.name.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <Icon className="h-4 w-4 shrink-0" />
                  {item.name}
                </button>
              );
            })}
          </nav>

          {/* Profile at bottom */}
          <div className="p-3 border-t border-[#E8E8E8]">
            <div className="flex items-center gap-2 px-2 mb-2">
              {user?.picture ? (
                <img src={user.picture} alt="" className="w-7 h-7 rounded-full shrink-0" />
              ) : (
                <div className="w-7 h-7 rounded-full bg-[#32B4AC] text-white flex items-center justify-center text-[11px] font-semibold shrink-0">
                  {user?.name?.charAt(0).toUpperCase()}
                </div>
              )}
              <div className="min-w-0">
                <p className="text-[12px] font-medium text-[#0F0F0F] truncate" data-testid="user-name">{user?.name}</p>
                {user?.role && (
                  <span className={`text-[10px] font-medium ${user.role === "admin" ? "text-[#32B4AC]" : "text-[#1A1A1A]/50"}`}>{user.role.toUpperCase()}</span>
                )}
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-[12px] text-red-500 hover:bg-red-50 transition-colors"
              data-testid="logout-button"
            >
              <LogOut className="h-3.5 w-3.5" />
              Logout
            </button>
          </div>
        </aside>

        {/* ===== MOBILE SIDEBAR OVERLAY ===== */}
        {mobileSidebarOpen && (
          <div className="fixed inset-0 z-50 md:hidden">
            <div className="fixed inset-0 bg-black/40" onClick={() => setMobileSidebarOpen(false)} />
            <aside className="fixed inset-y-0 left-0 w-56 bg-white shadow-xl flex flex-col">
              <div className="flex items-center justify-between h-14 px-4 border-b border-[#E8E8E8]">
                <span className="text-[15px] font-bold text-[#32B4AC]" style={{ fontFamily: "Manrope, sans-serif" }}>OMNIX</span>
                <button onClick={() => setMobileSidebarOpen(false)}><X className="h-5 w-5 text-[#1A1A1A]" /></button>
              </div>
              <nav className="flex-1 px-2 py-3 space-y-0.5 overflow-y-auto">
                {visibleHeaderTabs.map((tab) => {
                  const active = isActive(tab.href);
                  return (
                    <button key={tab.name} onClick={() => { navigate(tab.href); setMobileSidebarOpen(false); }}
                      className={`w-full flex items-center px-3 py-2 rounded-md text-[13px] font-medium transition-colors ${active ? "bg-[#32B4AC] text-white" : "text-[#1A1A1A] hover:bg-[#E8E8E8]/60"}`}
                    >{tab.name}</button>
                  );
                })}
                {visibleMoreTabs.map((tab) => {
                  const active = isActive(tab.href);
                  return (
                    <button key={tab.name} onClick={() => { navigate(tab.href); setMobileSidebarOpen(false); }}
                      className={`w-full flex items-center px-3 py-2 rounded-md text-[13px] font-medium transition-colors ${active ? "bg-[#32B4AC] text-white" : "text-[#1A1A1A] hover:bg-[#E8E8E8]/60"}`}
                    >{tab.name}</button>
                  );
                })}
                <div className="h-px bg-[#E8E8E8] my-2" />
                {sidebarItems.map((item) => {
                  const Icon = item.icon;
                  const active = isActive(item.href);
                  return (
                    <button key={item.name} onClick={() => { navigate(item.href); setMobileSidebarOpen(false); }}
                      className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-md text-[13px] font-medium text-left transition-colors ${active ? "bg-[#32B4AC] text-white" : "text-[#1A1A1A] hover:bg-[#E8E8E8]/60"}`}
                    ><Icon className="h-4 w-4 shrink-0" />{item.name}</button>
                  );
                })}
                <div className="h-px bg-[#E8E8E8] my-2" />
                <button onClick={() => { navigate("/quotations/create"); setMobileSidebarOpen(false); }}
                  className="w-full flex items-center px-3 py-2 rounded-md text-[13px] font-medium text-[#32B4AC]"
                >Create Quotation</button>
              </nav>
              <div className="p-3 border-t border-[#E8E8E8]">
                <div className="flex items-center gap-2 px-2 mb-2">
                  {user?.picture ? <img src={user.picture} alt="" className="w-7 h-7 rounded-full" /> : (
                    <div className="w-7 h-7 rounded-full bg-[#32B4AC] text-white flex items-center justify-center text-[11px] font-semibold">{user?.name?.charAt(0).toUpperCase()}</div>
                  )}
                  <p className="text-[12px] font-medium text-[#0F0F0F] truncate">{user?.name}</p>
                </div>
                <button onClick={handleLogout} className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-[12px] text-red-500 hover:bg-red-50">
                  <LogOut className="h-3.5 w-3.5" />Logout
                </button>
              </div>
            </aside>
          </div>
        )}

        {/* ===== MAIN CONTENT ===== */}
        <main className="flex-1 overflow-y-auto">
          <div className="px-5 py-4">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
}
